import { useCallback, useMemo } from 'react';

import { type usePublisherCategorySelector } from '../../publisher-category-selector/hooks/usePublisherCategorySelector';
import { type useSchoolCategorySelector } from '../../school-category-selector/hooks/useSchoolCategorySelector';
import { type useSubjectCategorySelector } from '../../subject-category-selector/hooks/useSubjectCategorySelector';

type CategoryErrors = {
  schoolTypeId?: string;
  gradeId?: string;
  semesterId?: string;
  subject?: string;
  publisher?: string;
  customPublisher?: string;
};

/**
 * Category validation hook - 에러 병합 및 통합 검증
 *
 * 세 개의 하위 state(school, subject, publisher)의 에러를 병합하고
 * 통합 검증을 수행합니다.
 *
 * @example
 * const validation = useCategoryValidation({
 *   schoolState,
 *   subjectState,
 *   publisherState
 * });
 * const { success, messages } = validation.validate();
 */
export const useCategoryValidation = ({
  schoolState,
  subjectState,
  publisherState,
}: {
  schoolState: ReturnType<typeof useSchoolCategorySelector>;
  subjectState: ReturnType<typeof useSubjectCategorySelector>;
  publisherState: ReturnType<typeof usePublisherCategorySelector>;
}) => {
  const errors: CategoryErrors = useMemo(
    () => ({
      ...schoolState.errors,
      ...subjectState.errors,
      ...publisherState.errors,
    }),
    [schoolState.errors, subjectState.errors, publisherState.errors],
  );

  const validate = useCallback((): { success: boolean; messages: string[] } => {
    const schoolValid = schoolState.validate();
    const subjectValid = subjectState.validate();
    const publisherValid = publisherState.validate();

    const success = schoolValid && subjectValid && publisherValid;

    const messages: string[] = [];
    if (!schoolValid) messages.push('School information validation failed');
    if (!subjectValid) messages.push('Subject validation failed');
    if (!publisherValid) messages.push('Publisher validation failed');

    return { success, messages };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolState.validate, subjectState.validate, publisherState.validate]);

  return {
    errors,
    validate,
  };
};
